import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  FolderOpen,
  Loader2,
  MinusCircle,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';
import {
  spaceErrorMessage,
  spacesApi,
  type SpaceFileDetail,
  type SpaceFileFeed,
  type SpaceIndexStatus,
  type SpaceNodeView,
} from '../../lib/api';
import { useResizablePanel } from '../../lib/resizable';
import { INDEX_STATUS_LABEL, copyText, useRefreshWhileIndexing } from '../../lib/spaces';
import { cn, timeAgo } from '../../lib/utils';
import { FileDetail } from '../spaces/FilePanel';
import { NodeIcon } from '../spaces/FolderView';
import { ResizeHandle } from '../ui/resize-handle';

/** A page of the list, and the most the server re-reads at once when the list is refreshed in place. */
const PAGE = 30;
const REFRESH_MAX = 200;
/** The spaces worker asks again for a file still being read after this long; past it, the wait is unusual. */
const SLOW_INDEX_MS = 10 * 60_000;

/**
 * The filters, each a set of index states. Waiting and being read are one choice because to a person
 * they are one question — is it searchable yet — and an image and a file too large to read are another,
 * since both are found by name only.
 */
const STATUS_FILTERS: Array<{ key: string; label: string; statuses: SpaceIndexStatus[] }> = [
  { key: '', label: 'All', statuses: [] },
  { key: 'indexing', label: 'Indexing', statuses: ['NONE', 'PENDING'] },
  { key: 'failed', label: 'Failed', statuses: ['FAILED'] },
  { key: 'partial', label: 'Partly indexed', statuses: ['PARTIAL'] },
  { key: 'indexed', label: 'Indexed', statuses: ['INDEXED'] },
  { key: 'unsearchable', label: 'Not searchable', statuses: ['UNSUPPORTED', 'TOO_LARGE'] },
];

/** The Feed's own pill, in the same colours: waiting amber, being read orange, done green, a failure red. */
const STATUS_STYLES: Record<SpaceIndexStatus, { icon: LucideIcon; tone: string; spin?: boolean }> = {
  NONE: { icon: Clock, tone: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300' },
  PENDING: { icon: Loader2, tone: 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300', spin: true },
  INDEXED: { icon: CheckCircle2, tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300' },
  PARTIAL: { icon: AlertTriangle, tone: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300' },
  UNSUPPORTED: { icon: MinusCircle, tone: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-500/20 dark:text-zinc-400' },
  TOO_LARGE: { icon: MinusCircle, tone: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-500/20 dark:text-zinc-400' },
  FAILED: { icon: AlertTriangle, tone: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300' },
};

/** The sentence behind a pill, shown as its tooltip. */
function statusDetail(file: SpaceNodeView): string {
  switch (file.indexStatus) {
    case 'NONE':
      return 'Not handed to the index yet. The spaces worker picks it up within a minute.';
    case 'PENDING': {
      if (!file.indexRequestedAt) return 'Being read for search.';
      const waited = Date.now() - new Date(file.indexRequestedAt).getTime();
      return `Being read for search, asked for ${timeAgo(file.indexRequestedAt)}.${waited > SLOW_INDEX_MS ? ' That is longer than usual.' : ''}`;
    }
    case 'INDEXED':
      return 'Its text is searchable.';
    case 'PARTIAL':
      return file.indexError ?? 'Searchable by its words; some passages are not yet searchable by meaning. Tried again hourly.';
    case 'UNSUPPORTED':
      return file.indexError ?? 'This kind of file has no text to read. It is found by its name only.';
    case 'TOO_LARGE':
      return file.indexError ?? 'Too large to read. It is found by its name only.';
    case 'FAILED':
      return file.indexError ?? 'Its text could not be read.';
    default:
      return '';
  }
}

function IndexPill({ file }: { file: SpaceNodeView }) {
  if (!file.indexStatus) return null;
  const style = STATUS_STYLES[file.indexStatus];
  const Icon = style.icon;
  return (
    <span
      title={statusDetail(file)}
      className={cn(
        'inline-flex flex-shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap',
        style.tone,
      )}
    >
      <Icon size={10} strokeWidth={2.25} className={style.spin ? 'animate-spin' : undefined} />
      {file.indexStatus === 'NONE' ? 'waiting' : INDEX_STATUS_LABEL[file.indexStatus]}
    </span>
  );
}

/** The folder an address sits in: `~/research/q3/notes.md` → `~/research/q3/`. */
function folderOf(address: string): string {
  return address.slice(0, address.lastIndexOf('/') + 1);
}

/**
 * The Feed's other view: the files in your personal space and in folders shared with you, most
 * recently changed first, each with where it is in being read for search — the `space-index` queue,
 * as far as it concerns files you can see.
 *
 * Not the knowledge base and not dressed as it: nothing here is ingested, the project scope does not
 * apply, and the list is what `GET /spaces/files` says this person can see — the scope their own
 * search reaches — so it never shows anybody else's files, a system admin's view included. The filter
 * and the open file live in the URL (`status`, `file`) like the other view's.
 */
export function SpaceFilesFeed({ viewSwitch }: { viewSwitch: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = STATUS_FILTERS.find((f) => f.key === (searchParams.get('status') ?? '')) ?? STATUS_FILTERS[0]!;
  const selectedId = searchParams.get('file');

  const [files, setFiles] = useState<SpaceNodeView[]>([]);
  const [counts, setCounts] = useState<SpaceFileFeed['counts'] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<SpaceFileDetail | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Only the newest read lands: a refresh started while a page was loading must not be overwritten by it.
  const request = useRef(0);
  const shown = useRef(PAGE);
  shown.current = Math.min(Math.max(PAGE, files.length), REFRESH_MAX);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const params = new URLSearchParams(searchParams);
      if (value) params.set(key, value);
      else params.delete(key);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /** Reads the list again from the top, as many rows as are showing, so a refresh keeps the scroll. */
  const load = useCallback(
    async (size: number) => {
      const id = ++request.current;
      try {
        const page = await spacesApi.recentFiles({ status: filter.statuses, limit: size });
        if (id !== request.current) return;
        setFiles(page.files);
        setCounts(page.counts);
        setCursor(page.nextCursor);
        setError(null);
      } catch (err) {
        if (id === request.current) setError(spaceErrorMessage(err, 'Could not list your files'));
      }
    },
    [filter],
  );

  useEffect(() => {
    setLoading(true);
    void load(PAGE).finally(() => setLoading(false));
  }, [load]);

  const refresh = useCallback(() => void load(shown.current), [load]);
  const refreshNow = () => {
    setRefreshing(true);
    void load(shown.current).finally(() => setRefreshing(false));
  };
  // Re-read while anything you can see is still waiting, so a row turns from "indexing" to "indexed" on its own.
  const waitingCount = counts ? counts.NONE + counts.PENDING : 0;
  useRefreshWhileIndexing(waitingCount > 0, refresh, filter.key);

  const loadMore = useCallback(() => {
    if (!cursor || loading || loadingMore) return;
    const id = request.current;
    setLoadingMore(true);
    spacesApi
      .recentFiles({ status: filter.statuses, cursor, limit: PAGE })
      .then((page) => {
        if (id !== request.current) return;
        setFiles((prev) => {
          const held = new Set(prev.map((f) => f.id));
          return [...prev, ...page.files.filter((f) => !held.has(f.id))];
        });
        setCounts(page.counts);
        setCursor(page.nextCursor);
      })
      .catch((err) => id === request.current && setError(spaceErrorMessage(err, 'Could not list your files')))
      .finally(() => setLoadingMore(false));
  }, [cursor, loading, loadingMore, filter]);

  useEffect(() => {
    const el = sentinelRef.current;
    const root = scrollerRef.current;
    if (!el || !root) return;
    const observer = new IntersectionObserver((entries) => entries[0]?.isIntersecting && loadMore(), { root, rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  useEffect(() => setDetail(null), [selectedId]);

  // The open file is re-read by its own panel while it is being indexed; what it reads is folded back
  // into its row, so the row and the pane never disagree about whether it is searchable.
  const onLoaded = useCallback((next: SpaceFileDetail) => {
    setDetail(next);
    setFiles((prev) =>
      prev.map((f) => (f.id === next.id ? { ...f, name: next.name, address: next.address, indexStatus: next.indexStatus, indexError: next.indexError } : f)),
    );
  }, []);

  const flash = useCallback((message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice((current) => (current === message ? null : current)), 2500);
  }, []);
  const copyPath = useCallback(
    async (address: string) => {
      flash((await copyText(address)) ? `Copied ${address}` : `Could not copy to the clipboard. The path is ${address}`);
    },
    [flash],
  );

  const listPanel = useResizablePanel({ initial: 400, min: 300, max: 620, side: 'left', storageKey: 'feed.list.width' });

  const total = counts ? Object.values(counts).reduce((sum, n) => sum + n, 0) : 0;
  const countOf = (statuses: SpaceIndexStatus[]) =>
    counts ? (statuses.length === 0 ? total : statuses.reduce((sum, s) => sum + counts[s], 0)) : null;
  const selectedRow = files.find((f) => f.id === selectedId) ?? null;
  // The row carries when it was asked for, which a file's own detail does not; the detail is fresher.
  const header: SpaceNodeView | null = detail && detail.id === selectedId ? { ...selectedRow, ...detail } : selectedRow;

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <div className="pl-[var(--sidebar-w)] pr-[var(--rail-w)] flex-1 h-full flex overflow-hidden">
        <aside style={{ width: listPanel.width }} className="flex h-full flex-shrink-0 flex-col border-r border-border bg-card">
          <div className="border-b border-border-subtle px-4 pt-5 pb-3">
            <div className="flex items-baseline gap-2">
              <h1 className="text-lg font-bold text-ink">Feed</h1>
              <span className="ml-auto text-[10px] tabular-nums text-ink-faint">{files.length} shown</span>
              <button
                type="button"
                onClick={refreshNow}
                disabled={refreshing}
                title="Read the list again"
                aria-label="Refresh the list"
                className="self-center text-ink-faint transition-colors hover:text-ink disabled:opacity-50"
              >
                <RefreshCw size={12} className={refreshing ? 'animate-spin' : undefined} />
              </button>
            </div>
            {viewSwitch && <div className="mt-2">{viewSwitch}</div>}
            <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">
              Files in your space and in folders shared with you, newest first, and whether each has been read for search. Nothing
              here goes into the knowledge base.
            </p>

            {counts && (
              // The queue in one line: what is still on its way and what went wrong, or that nothing is.
              <p className="mt-2 text-[11px] text-ink-muted">
                {waitingCount === 0 && counts.FAILED === 0 ? (
                  'Nothing is waiting to be indexed.'
                ) : (
                  <>
                    <span className={cn('font-medium', waitingCount > 0 ? 'text-ink' : 'text-ink-faint')}>{waitingCount}</span> waiting to
                    be indexed ·{' '}
                    <span className={cn('font-medium', counts.FAILED > 0 ? 'text-danger-text' : 'text-ink-faint')}>{counts.FAILED}</span>{' '}
                    failed
                  </>
                )}
              </p>
            )}

            <div className="mt-3 flex flex-wrap gap-1">
              {STATUS_FILTERS.map((f) => {
                const n = countOf(f.statuses);
                return (
                  <button
                    key={f.key}
                    onClick={() => setParam('status', f.key || null)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                      filter.key === f.key
                        ? 'border-orange-500 bg-orange-500 text-white'
                        : 'border-border bg-surface-subtle text-ink-muted hover:border-orange-300 hover:text-ink',
                    )}
                  >
                    {f.label}
                    {n !== null && <span className="ml-1 tabular-nums opacity-75">{n}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="border-b border-danger-ring bg-danger-bg px-4 py-2.5 text-[11px] text-danger-text">{error}</div>
          )}

          <div ref={scrollerRef} className="flex-1 overflow-y-auto">
            {loading ? (
              <p className="px-4 py-12 text-center text-[11px] text-ink-faint">Loading…</p>
            ) : files.length === 0 ? (
              <div className="px-4 py-12 text-center text-[11px] text-ink-faint">
                <FolderOpen size={18} className="mx-auto mb-2 opacity-60" />
                {filter.key ? (
                  'No files in this state'
                ) : (
                  <>
                    No files in your space or shared with you yet.
                    <Link to="/spaces" className="mt-1 block font-medium text-ink-muted hover:text-ink">
                      Open Spaces
                    </Link>
                  </>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border-subtle">
                {files.map((file) => (
                  <SpaceFileRow key={file.id} file={file} active={file.id === selectedId} onOpen={() => setParam('file', file.id)} />
                ))}
              </div>
            )}

            {!loading && files.length > 0 && (
              <div ref={sentinelRef} className="py-4 text-center text-[10px] text-ink-faint">
                {loadingMore ? 'Loading more…' : cursor ? '' : 'End of files'}
              </div>
            )}
          </div>
        </aside>

        <ResizeHandle handleProps={listPanel.handleProps} onDoubleClick={listPanel.reset} className="bg-card" />

        <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-card">
          {selectedId ? (
            <>
              <header className="flex-shrink-0 border-b border-border px-7 py-4">
                <div className="flex items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <h2 className="truncate text-sm font-semibold text-ink">{header?.name ?? 'File'}</h2>
                    {header && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <IndexPill file={header} />
                        <span className="truncate font-mono text-[11px] text-ink-muted">{folderOf(header.address)}</span>
                      </div>
                    )}
                    {notice && <p className="mt-1.5 text-[11px] text-ink-muted">{notice}</p>}
                  </div>
                  {header?.parentId && (
                    <Link
                      to={`/spaces?folder=${encodeURIComponent(header.parentId)}&file=${encodeURIComponent(header.id)}`}
                      title="Open this file in its folder, where it can be renamed, moved, edited or shared"
                      className="flex flex-shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-[11px] font-medium text-ink-muted transition-colors hover:border-border-strong hover:text-ink"
                    >
                      <ExternalLink size={12} />
                      Open in Spaces
                    </Link>
                  )}
                </div>
              </header>
              <div className="flex-1 overflow-y-auto">
                <div className="px-7 py-6">
                  <FileDetail fileId={selectedId} onCopyPath={(address) => void copyPath(address)} onLoaded={onLoaded} />
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-full items-center justify-center px-8">
              <div className="text-center">
                <FolderOpen size={22} className="mx-auto mb-3 text-ink-faint opacity-60" />
                <p className="text-xs text-ink-muted">Pick a file on the left to see it.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** One file, one line about it and one about where it is — the Feed's row, for a file. */
function SpaceFileRow({ file, active, onOpen }: { file: SpaceNodeView; active: boolean; onOpen: () => void }) {
  const problem = (file.indexStatus === 'FAILED' || file.indexStatus === 'PARTIAL') && file.indexError ? file.indexError : null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'block w-full border-l-2 px-4 py-3 text-left transition-colors',
        active ? 'border-l-orange-500 bg-surface-muted' : 'border-l-transparent hover:bg-surface-subtle',
      )}
    >
      <div className="flex items-center gap-2">
        <NodeIcon node={file} />
        <span className={cn('truncate text-xs font-medium', active ? 'text-ink' : 'text-ink-secondary')}>{file.name}</span>
        <span className="ml-auto flex flex-shrink-0 items-center gap-2">
          <IndexPill file={file} />
          <span className="whitespace-nowrap text-[10px] text-ink-faint">{timeAgo(file.updatedAt)}</span>
        </span>
      </div>
      <p className="mt-1 truncate pl-[22px] font-mono text-[10px] text-ink-faint">
        {folderOf(file.address)}
        {file.modifiedBy && <span className="font-sans"> · {file.modifiedBy}</span>}
      </p>
      {problem && (
        <p className={cn('mt-0.5 truncate pl-[22px] text-[11px]', file.indexStatus === 'FAILED' ? 'text-danger-text' : 'text-ink-faint')}>
          {problem}
        </p>
      )}
    </button>
  );
}
